import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { SectionHeader } from '@/components/SectionHeader';
import { Heart, Quote, Send, Loader2, Sparkles, Smile, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const moods = [
  { key: 'great', label: 'Great', emoji: '🔥', color: 'from-emerald-500 to-green-600', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  { key: 'good', label: 'Good', emoji: '😊', color: 'from-blue-500 to-cyan-600', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
  { key: 'okay', label: 'Okay', emoji: '😐', color: 'from-amber-500 to-yellow-600', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  { key: 'stressed', label: 'Stressed', emoji: '😰', color: 'from-orange-500 to-red-600', bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30' },
  { key: 'bad', label: 'Bad', emoji: '😞', color: 'from-red-500 to-rose-600', bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
];

const WELLNESS_TIPS = {
  great: [
    ["Channel your energy into mentoring a teammate today", "Share what's making you feel great with the team", "Use this momentum to tackle a challenging task"],
    ["Celebrate small wins - you've earned it!", "Your positive energy is contagious, spread it around", "Set an ambitious goal while you're feeling unstoppable"],
    ["Write down 3 things you're grateful for right now", "Help a colleague who might be struggling today", "Take on that task you've been procrastinating on"],
  ],
  good: [
    ["Keep the good vibes going with a short walk", "Reach out to a teammate and share something positive", "Focus on your top priority while your energy is up"],
    ["Listen to your favorite music to maintain the mood", "Take a moment to appreciate your progress this week", "Stay consistent - good days build great results"],
    ["Organize your workspace for even better focus", "Set a small reward for completing your next task", "Share a kind word with someone today"],
  ],
  okay: [
    ["A short 5-minute stretch can shift your energy", "Break your next task into smaller, manageable steps", "Have a cup of tea or coffee and reset your focus"],
    ["It's okay to have average days - be kind to yourself", "Try the Pomodoro technique: 25 min work, 5 min break", "Write down one thing you want to accomplish today"],
    ["Step outside for fresh air if possible", "Connect with a friend or colleague for a quick chat", "Remember: progress, not perfection, is the goal"],
  ],
  stressed: [
    ["Take 3 deep breaths: inhale 4s, hold 4s, exhale 6s", "Write down what's stressing you - getting it out helps", "Focus on one thing at a time, not everything at once"],
    ["Step away from the screen for 10 minutes", "Talk to someone you trust about how you're feeling", "Prioritize your tasks - not everything is urgent"],
    ["Try a quick 5-minute meditation or breathing exercise", "Remember: this feeling is temporary and will pass", "Ask for help if your workload is overwhelming"],
  ],
  bad: [
    ["It's okay to not be okay. Be gentle with yourself", "Reach out to a trusted friend, family, or colleague", "Focus only on the essentials today - give yourself grace"],
    ["Take a longer break if you need it - recovery matters", "Write in a journal to process your thoughts", "Do one small thing that usually makes you smile"],
    ["Remember your past wins - you've overcome tough days before", "Consider going for a walk to clear your mind", "Don't push too hard - tomorrow is a new opportunity"],
  ],
  default: [
    ["Take a 5-min break every hour to refresh your mind", "Stay hydrated - drink water regularly", "Share positive vibes with your team"],
    ["Stretch your body between tasks for better focus", "Set clear goals for the day and celebrate small wins", "Remember to eat well and take care of yourself"],
    ["Practice gratitude - think of 3 good things today", "Connect with your teammates and check in on them", "End the day by reviewing what you accomplished"],
  ],
};

function WellnessTipsCard({ mood }) {
  const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * 3));
  const tipSet = WELLNESS_TIPS[mood] || WELLNESS_TIPS.default;
  const tips = tipSet[tipIndex % tipSet.length];
  const moodColors = {
    great: { bg: 'from-emerald-500/10 to-green-500/10', border: 'border-emerald-500/20', accent: 'text-emerald-400' },
    good: { bg: 'from-blue-500/10 to-cyan-500/10', border: 'border-blue-500/20', accent: 'text-blue-400' },
    okay: { bg: 'from-amber-500/10 to-yellow-500/10', border: 'border-amber-500/20', accent: 'text-amber-400' },
    stressed: { bg: 'from-orange-500/10 to-red-500/10', border: 'border-orange-500/20', accent: 'text-orange-400' },
    bad: { bg: 'from-red-500/10 to-rose-500/10', border: 'border-red-500/20', accent: 'text-red-400' },
  };
  const colors = moodColors[mood] || { bg: 'from-violet-500/10 to-indigo-500/10', border: 'border-violet-500/20', accent: 'text-violet-400' };

  return (
    <div className={`bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-xl p-6`} data-testid="wellness-tips-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-sm font-semibold ${colors.accent}`}>
          {mood ? `Wellness Tips for "${mood}" mood` : 'Wellness Tips'}
        </h3>
        <button
          onClick={() => setTipIndex((tipIndex + 1) % tipSet.length)}
          className={`p-1.5 rounded-lg hover:bg-white/5 transition-colors ${colors.accent}`}
          data-testid="rotate-tips-btn"
          title="Show different tips"
        >
          <RefreshCw size={14} />
        </button>
      </div>
      <div className="space-y-3 text-[13px] text-[hsl(var(--text-muted))]">
        {tips.map((tip, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className={`${colors.accent} mt-0.5 font-medium`}>{i + 1}.</span>
            <p>{tip}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MoodQuotesPage() {
  const [loading, setLoading] = useState(true);
  const [todayQuote, setTodayQuote] = useState(null);
  const [myQuote, setMyQuote] = useState('');
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [allQuotes, setAllQuotes] = useState([]);
  // Mood state
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodNote, setMoodNote] = useState('');
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [existingEntry, setExistingEntry] = useState(null);
  const [submittingMood, setSubmittingMood] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const [quoteRes, allQuotesRes, moodRes] = await Promise.all([
        axios.get(`${API}/quotes/today`),
        axios.get(`${API}/quotes/all`),
        axios.get(`${API}/mood/today`, h)
      ]);
      setTodayQuote(quoteRes.data.quote);
      setAllQuotes(allQuotesRes.data.quotes);
      setAlreadySubmitted(moodRes.data.already_submitted);
      setExistingEntry(moodRes.data.existing_entry);
      if (moodRes.data.existing_entry) {
        setSelectedMood(moodRes.data.existing_entry.mood);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMood = async () => {
    if (!selectedMood) { toast.error('Please select your mood'); return; }
    setSubmittingMood(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/mood/submit`, { mood: selectedMood, note: moodNote }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(alreadySubmitted ? 'Mood updated!' : 'Mood submitted!');
      setAlreadySubmitted(true);
      setMoodNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit mood');
    } finally {
      setSubmittingMood(false);
    }
  };

  const handleSubmitQuote = async () => {
    if (!myQuote.trim()) { toast.error('Please enter a quote'); return; }
    setSubmittingQuote(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/quotes/submit`, { quote: myQuote }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Quote submitted!');
      setMyQuote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setSubmittingQuote(false);
    }
  };

  if (loading) return <ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div></ResponsiveLayout>;

  const currentMoodInfo = moods.find(m => m.key === selectedMood);

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader
          icon={Heart}
          title="Daily Ecstasy"
          subtitle="Check in your mood & share inspirational quotes"
          iconBg="bg-gradient-to-br from-violet-500 to-indigo-600 shadow-violet-500/20"
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Mood & Quotes */}
          <div className="lg:col-span-2 space-y-6">
            {/* Mood Check-in */}
            <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Smile size={20} className="text-violet-400" />
                <h2 className="font-semibold">Mood Check-in</h2>
                {alreadySubmitted && <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs ml-2">Submitted</Badge>}
              </div>

              <p className="text-sm text-[hsl(var(--text-muted))] mb-4">How are you feeling today?</p>

              {/* Mood selector */}
              <div className="grid grid-cols-5 gap-2 mb-4" data-testid="mood-selector">
                {moods.map((mood) => (
                  <button
                    key={mood.key}
                    data-testid={`mood-${mood.key}`}
                    onClick={() => setSelectedMood(mood.key)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all ${
                      selectedMood === mood.key
                        ? `${mood.bg} ${mood.border} ring-1 ring-offset-0 ${mood.border}`
                        : 'border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]'
                    }`}
                  >
                    <span className="text-2xl">{mood.emoji}</span>
                    <span className={`text-[11px] font-medium ${selectedMood === mood.key ? mood.text : 'text-[hsl(var(--text-muted))]'}`}>{mood.label}</span>
                  </button>
                ))}
              </div>

              {selectedMood && (
                <div className="space-y-3">
                  <Input
                    data-testid="mood-note-input"
                    value={moodNote}
                    onChange={(e) => setMoodNote(e.target.value)}
                    placeholder="Add a note (optional)..."
                    className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"
                  />
                  <Button
                    data-testid="submit-mood-btn"
                    onClick={handleSubmitMood}
                    disabled={submittingMood}
                    className={`bg-gradient-to-r ${currentMoodInfo?.color || 'from-violet-500 to-indigo-600'} text-white`}
                  >
                    {submittingMood ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                    {alreadySubmitted ? 'Update Mood' : 'Submit Mood'}
                  </Button>
                </div>
              )}
            </div>

            {/* Quote of the Day */}
            <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Quote size={20} className="text-amber-400" />
                <h2 className="font-semibold">Quote of the Day</h2>
              </div>
              {todayQuote ? (
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-6 mb-4">
                  <p className="text-lg italic mb-3">"{todayQuote.quote}"</p>
                  <p className="text-sm text-[hsl(var(--text-muted))]">- {todayQuote.user_name}</p>
                </div>
              ) : (
                <div className="bg-[hsl(var(--surface))] rounded-xl p-6 mb-4 text-center">
                  <Sparkles size={32} className="mx-auto text-[hsl(var(--text-muted))] mb-2" />
                  <p className="text-[hsl(var(--text-muted))]">No quote selected for today yet</p>
                </div>
              )}

              {/* Submit Quote */}
              <div className="space-y-3">
                <p className="text-sm text-[hsl(var(--text-secondary))]">Share an inspirational quote:</p>
                <div className="flex gap-2">
                  <Input
                    data-testid="quote-input"
                    value={myQuote} onChange={(e) => setMyQuote(e.target.value)}
                    placeholder="Enter your quote..."
                    className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmitQuote()}
                  />
                  <Button onClick={handleSubmitQuote} disabled={submittingQuote} className="bg-amber-500 hover:bg-amber-600" data-testid="submit-quote-btn">
                    {submittingQuote ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Team Quotes & Wellness */}
          <div className="space-y-6">
            {/* Current Mood Status */}
            {alreadySubmitted && existingEntry && (
              <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6">
                <h3 className="text-sm font-semibold text-[hsl(var(--text-secondary))] mb-3">Your Mood Today</h3>
                <div className="text-center py-4">
                  <span className="text-5xl">{moods.find(m => m.key === existingEntry.mood)?.emoji || '?'}</span>
                  <p className={`text-lg font-semibold mt-2 ${moods.find(m => m.key === existingEntry.mood)?.text || ''}`}>
                    {moods.find(m => m.key === existingEntry.mood)?.label || existingEntry.mood}
                  </p>
                  {existingEntry.note && <p className="text-sm text-[hsl(var(--text-muted))] mt-1 italic">"{existingEntry.note}"</p>}
                  <p className="text-[10px] text-[hsl(var(--text-muted))] mt-2">
                    {existingEntry.period === 'morning' ? 'Morning check-in' : 'Afternoon check-in'}
                  </p>
                </div>
              </div>
            )}

            {/* Team Quotes */}
            {allQuotes.length > 0 && (
              <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6">
                <h3 className="text-sm font-semibold text-[hsl(var(--text-secondary))] mb-3">Team Quotes Today ({allQuotes.length})</h3>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {allQuotes.map((q) => (
                    <div key={q.id} className={`p-3 rounded-lg text-sm ${q.is_selected ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-[hsl(var(--surface))]'}`}>
                      <p className="text-[13px]">"{q.quote}"</p>
                      <p className="text-xs text-[hsl(var(--text-muted))] mt-1">- {q.user_name} {q.is_selected && <Badge className="ml-2 bg-amber-500/30 text-amber-400 text-[10px]">Selected</Badge>}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Tips Card - Rotating & Mood-based */}
            <WellnessTipsCard mood={selectedMood} />
          </div>
        </div>
      </div>
    </ResponsiveLayout>
  );
}
